import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { api } from './client';

export interface PasskeyCredential {
  id: string;
  deviceName: string;
  createdAt: number;
  lastUsedAt: number | null;
}

export async function getRegistrationOptions() {
  const response = await api.post('/user/webauthn/register-options');
  return response.data;
}

export async function registerPasskey(deviceName?: string) {
  // 1. Get options from server
  const options = await getRegistrationOptions();

  // 2. Create credential via browser API
  const credential = await startRegistration({ optionsJSON: options });

  // 3. Verify with server
  const response = await api.post('/user/webauthn/register-verify', {
    credential,
    deviceName,
  });

  return response.data;
}

export async function getAuthenticationOptions() {
  const response = await api.post('/user/webauthn/authenticate-options');
  return response.data;
}

export async function authenticateWithPasskey() {
  // 1. Get options from server
  const options = await getAuthenticationOptions();

  // 2. Authenticate via browser API
  const credential = await startAuthentication({ optionsJSON: options });

  // 3. Verify with server
  const response = await api.post('/user/webauthn/authenticate-verify', {
    credential,
  });

  return response.data;
}

export async function listPasskeys(): Promise<PasskeyCredential[]> {
  const response = await api.get('/user/webauthn/credentials');
  return response.data.credentials;
}

export async function deletePasskey(id: string) {
  const response = await api.delete(`/user/webauthn/credentials/${id}`);
  return response.data;
}

/**
 * Check if WebAuthn is supported by this browser/device.
 */
export function isWebAuthnSupported(): boolean {
  return !!(window.PublicKeyCredential);
}

/**
 * Check if the platform supports conditional mediation (autofill passkeys).
 */
export async function isConditionalMediationAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  try {
    return await (window.PublicKeyCredential as any).isConditionalMediationAvailable?.() ?? false;
  } catch {
    return false;
  }
}
